import React, { useCallback, useEffect, useId, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import {
  ArrowPathIcon,
  BanknotesIcon,
  ExclamationTriangleIcon,
  MagnifyingGlassIcon,
  PencilSquareIcon,
  PlusCircleIcon,
  XMarkIcon,
} from '@heroicons/react/24/solid';

import logo from '../assets/logo.png';
import { useAuth } from '../context/AuthContext';
import api from '../utils/api';
import { downloadReport } from '../utils/downloadReport';

const tabs = [
  { key: 'pending', label: 'Por aprobar', params: { estado: 'pendiente_aprobacion' } },
  { key: 'collect', label: 'Por cobrar', params: { estado: 'aprobado' } },
  { key: 'overdue', label: 'Vencidos', params: { overdue: 'true' } },
  { key: 'collected', label: 'Cobrados', params: { estado: 'cobrado' } },
];

const monthOptions = [
  ['1', 'Enero'],
  ['2', 'Febrero'],
  ['3', 'Marzo'],
  ['4', 'Abril'],
  ['5', 'Mayo'],
  ['6', 'Junio'],
  ['7', 'Julio'],
  ['8', 'Agosto'],
  ['9', 'Septiembre'],
  ['10', 'Octubre'],
  ['11', 'Noviembre'],
  ['12', 'Diciembre'],
];

const toMoney = (value) => {
  const number = Number(value);
  return `$${Number.isFinite(number) ? number.toFixed(2) : '0.00'}`;
};

const normalizeText = (value) =>
  String(value || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toUpperCase()
    .trim()
    .replace(/\s+/g, ' ');

const currentYear = new Date().getFullYear();

const emptyCompany = { nombre: '', ruc: '', email: '', telefono: '', activo: true };
const emptyResponsible = { company: '', nombre: '', departamento: '', email: '', telefono: '', activo: true };

const compareText = (left, right) => {
  const normalizedLeft = normalizeText(left);
  const normalizedRight = normalizeText(right);
  if (normalizedLeft < normalizedRight) return -1;
  if (normalizedLeft > normalizedRight) return 1;
  return 0;
};

const compareCatalogIds = (left, right) => Number(left.id) - Number(right.id);
const compareCompanies = (left, right) => compareText(left.nombre, right.nombre) || compareCatalogIds(left, right);
const compareResponsibles = (left, right) => (
  compareText(left.nombre, right.nombre)
  || compareText(left.departamento, right.departamento)
  || compareCatalogIds(left, right)
);

const upsertCatalogItem = (items, savedItem, compareItems) => {
  const exists = items.some((item) => String(item.id) === String(savedItem.id));
  const updatedItems = exists
    ? items.map((item) => (String(item.id) === String(savedItem.id) ? savedItem : item))
    : [...items, savedItem];
  return [...updatedItems].sort(compareItems);
};

const replaceSummaryLabel = (currentSummary, bucketName, id, label) => {
  if (!currentSummary) return currentSummary;
  return {
    ...currentSummary,
    [bucketName]: (currentSummary[bucketName] || []).map((item) => (
      String(item.key) === String(id) ? { ...item, label } : item
    )),
  };
};

const CreditReceivables = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const tabParam = searchParams.get('tab');
  const { user, loading } = useAuth() || { user: null, loading: true };
  const [activeTab, setActiveTab] = useState('pending');
  const [companies, setCompanies] = useState([]);
  const [responsibles, setResponsibles] = useState([]);
  const [matrizadores, setMatrizadores] = useState([]);
  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState(null);
  const [alerts, setAlerts] = useState(null);
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [loadingData, setLoadingData] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [filters, setFilters] = useState({
    fecha_documento: '',
    year: String(currentYear),
    month: '',
    start_date: '',
    end_date: '',
    company_id: '',
    responsible_id: '',
    usuario_matrizador: '',
  });
  const [companyModalOpen, setCompanyModalOpen] = useState(false);
  const [responsibleModalOpen, setResponsibleModalOpen] = useState(false);
  const [companyForm, setCompanyForm] = useState(emptyCompany);
  const [responsibleForm, setResponsibleForm] = useState(emptyResponsible);
  const [editingCompanyId, setEditingCompanyId] = useState(null);
  const [editingResponsibleId, setEditingResponsibleId] = useState(null);
  const [companySearch, setCompanySearch] = useState('');
  const [responsibleSearch, setResponsibleSearch] = useState('');
  const [catalogError, setCatalogError] = useState('');
  const [companySubmitting, setCompanySubmitting] = useState(false);
  const [responsibleSubmitting, setResponsibleSubmitting] = useState(false);
  const companySubmittingRef = useRef(false);
  const responsibleSubmittingRef = useRef(false);
  const companyCatalogRevisionRef = useRef(0);
  const responsibleCatalogRevisionRef = useRef(0);
  const tabRefs = useRef([]);
  const [reviewModal, setReviewModal] = useState(null);
  const [reviewNotes, setReviewNotes] = useState('');
  const [collectModalOpen, setCollectModalOpen] = useState(false);
  const [collectForm, setCollectForm] = useState({
    tipo_pago: 'transferencia',
    numero_transaccion: '',
    fecha_pago: new Date().toISOString().slice(0, 10),
    archivo: null,
    notes: '',
  });
  const [reportPeriod, setReportPeriod] = useState('month');

  useEffect(() => {
    if (!loading && (!user || !user.is_notario)) {
      toast.error('No tienes permisos para acceder a Trámites por Cobrar.');
      navigate('/login');
    }
  }, [user, loading, navigate]);

  useEffect(() => {
    if (tabs.some((item) => item.key === tabParam)) {
      setActiveTab(tabParam);
    }
  }, [tabParam]);

  const fetchCompanies = useCallback(async () => {
    const revision = companyCatalogRevisionRef.current;
    const companiesResponse = await api.get('/api/audit/credit-companies/');
    if (revision === companyCatalogRevisionRef.current) {
      setCompanies(Array.isArray(companiesResponse.data?.results) ? companiesResponse.data.results : []);
    }
  }, []);

  const fetchResponsibles = useCallback(async () => {
    const revision = responsibleCatalogRevisionRef.current;
    const responsiblesResponse = await api.get('/api/audit/credit-responsibles/');
    if (revision === responsibleCatalogRevisionRef.current) {
      setResponsibles(Array.isArray(responsiblesResponse.data?.results) ? responsiblesResponse.data.results : []);
    }
  }, []);

  const fetchMatrizadores = useCallback(async () => {
    const matrizadoresResponse = await api.get('/api/audit/matrizadores/list/');
    setMatrizadores(Array.isArray(matrizadoresResponse.data) ? matrizadoresResponse.data : []);
  }, []);

  const fetchCatalogs = useCallback(async () => {
    await Promise.all([fetchCompanies(), fetchResponsibles(), fetchMatrizadores()]);
  }, [fetchCompanies, fetchMatrizadores, fetchResponsibles]);

  const buildParams = useCallback(() => {
    const tab = tabs.find((item) => item.key === activeTab) || tabs[0];
    const params = { ...tab.params };
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== '') params[key] = value;
    });
    if (!params.month) {
      delete params.year;
      delete params.month;
    }
    if (params.fecha_documento) {
      delete params.year;
      delete params.month;
      delete params.start_date;
      delete params.end_date;
    }
    return params;
  }, [activeTab, filters]);

  const fetchReceivables = useCallback(async () => {
    setLoadingData(true);
    try {
      const [receivablesResponse, alertsResponse] = await Promise.all([
        api.get('/api/audit/credit-receivables/', { params: buildParams() }),
        api.get('/api/audit/credit-receivables/alerts/'),
      ]);
      setRows(Array.isArray(receivablesResponse.data?.results) ? receivablesResponse.data.results : []);
      setSummary(receivablesResponse.data?.summary || null);
      setAlerts(alertsResponse.data || null);
      setSelectedIds(new Set());
      setCurrentPage(1);
    } catch (err) {
      toast.error('No se pudieron cargar los trámites por cobrar.');
      setRows([]);
      setSummary(null);
    } finally {
      setLoadingData(false);
    }
  }, [buildParams]);

  useEffect(() => {
    if (!loading && user?.is_notario) {
      fetchCatalogs().catch(() => toast.error('No se pudieron cargar empresas/responsables.'));
    }
  }, [loading, user, fetchCatalogs]);

  useEffect(() => {
    if (!loading && user?.is_notario) {
      fetchReceivables();
    }
  }, [loading, user, activeTab, fetchReceivables]);

  const responsiblesForFilter = useMemo(() => {
    if (!filters.company_id) return responsibles;
    return responsibles.filter((responsible) => String(responsible.company) === String(filters.company_id));
  }, [filters.company_id, responsibles]);

  const filteredCompanies = useMemo(() => {
    const query = normalizeText(companySearch);
    if (!query) return companies;
    return companies.filter((company) => normalizeText(`${company.nombre} ${company.ruc} ${company.email}`).includes(query));
  }, [companies, companySearch]);

  const filteredResponsibles = useMemo(() => {
    const query = normalizeText(responsibleSearch);
    if (!query) return responsibles;
    return responsibles.filter((responsible) => normalizeText(
      `${responsible.nombre} ${responsible.departamento} ${responsible.company_nombre} ${responsible.email}`
    ).includes(query));
  }, [responsibles, responsibleSearch]);

  const selectedRows = useMemo(() => rows.filter((row) => selectedIds.has(String(row.id))), [rows, selectedIds]);
  const selectedTotal = useMemo(
    () => selectedRows.reduce((acc, row) => acc + (Number(row.monto) || 0), 0),
    [selectedRows]
  );

  const selectedPeriodLabel = useMemo(() => {
    const periods = new Set(selectedRows.map((row) => `${row.periodo_year}-${String(row.periodo_month).padStart(2, '0')}`));
    return periods.size === 1 ? Array.from(periods)[0] : '';
  }, [selectedRows]);

  const totalPages = useMemo(() => Math.max(1, Math.ceil(rows.length / pageSize)), [rows.length, pageSize]);
  const paginatedRows = useMemo(() => {
    const start = (currentPage - 1) * pageSize;
    return rows.slice(start, start + pageSize);
  }, [rows, currentPage, pageSize]);
  const pageStart = rows.length === 0 ? 0 : (currentPage - 1) * pageSize + 1;
  const pageEnd = Math.min(currentPage * pageSize, rows.length);

  useEffect(() => {
    if (currentPage > totalPages) {
      setCurrentPage(totalPages);
    }
  }, [currentPage, totalPages]);

  const toggleSelected = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      const key = String(id);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const canSelectRow = (row) => {
    if (row.estado === 'cobrado') return false;
    if (activeTab === 'pending') return row.estado === 'pendiente_aprobacion';
    if (activeTab === 'collect' || activeTab === 'overdue') return row.estado === 'aprobado';
    return false;
  };

  const reconcileCompany = useCallback((savedCompany) => {
    const normalizedCompany = { ...savedCompany, nombre: normalizeText(savedCompany.nombre) };
    setCompanies((current) => upsertCatalogItem(current, normalizedCompany, compareCompanies));
    setResponsibles((current) => current.map((responsible) => (
      String(responsible.company) === String(normalizedCompany.id)
        ? { ...responsible, company_nombre: normalizedCompany.nombre }
        : responsible
    )));
    setRows((current) => current.map((row) => (
      String(row.company?.id) === String(normalizedCompany.id)
        ? { ...row, company: { ...row.company, nombre: normalizedCompany.nombre } }
        : row
    )));
    setSummary((current) => replaceSummaryLabel(
      current,
      'by_company',
      normalizedCompany.id,
      normalizedCompany.nombre,
    ));
  }, []);

  const reconcileResponsible = useCallback((savedResponsible) => {
    const normalizedResponsible = {
      ...savedResponsible,
      nombre: normalizeText(savedResponsible.nombre),
      departamento: normalizeText(savedResponsible.departamento),
    };
    const label = normalizedResponsible.departamento
      ? `${normalizedResponsible.nombre} - ${normalizedResponsible.departamento}`
      : normalizedResponsible.nombre;
    setResponsibles((current) => upsertCatalogItem(current, normalizedResponsible, compareResponsibles));
    setRows((current) => current.map((row) => (
      String(row.responsible?.id) === String(normalizedResponsible.id)
        ? {
          ...row,
          responsible: {
            ...row.responsible,
            nombre: normalizedResponsible.nombre,
            departamento: normalizedResponsible.departamento,
          },
        }
        : row
    )));
    setSummary((current) => replaceSummaryLabel(
      current,
      'by_responsible',
      normalizedResponsible.id,
      label,
    ));
  }, []);

  const submitCompany = async (event) => {
    event.preventDefault();
    if (companySubmittingRef.current) return;
    companySubmittingRef.current = true;
    setCompanySubmitting(true);
    setCatalogError('');
    let response;
    try {
      if (editingCompanyId) {
        response = await api.patch('/api/audit/credit-companies/', { ...companyForm, id: editingCompanyId });
      } else {
        response = await api.post('/api/audit/credit-companies/', companyForm);
      }
    } catch (err) {
      const message = err?.response?.data?.error || 'No se pudo guardar la empresa.';
      setCatalogError(message);
      toast.error(message);
      return;
    } finally {
      companySubmittingRef.current = false;
      setCompanySubmitting(false);
    }

    const companyRevision = companyCatalogRevisionRef.current + 1;
    const responsibleRevision = responsibleCatalogRevisionRef.current + 1;
    companyCatalogRevisionRef.current = companyRevision;
    responsibleCatalogRevisionRef.current = responsibleRevision;
    reconcileCompany(response.data);
    setCompanyForm(emptyCompany);
    setEditingCompanyId(null);
    toast.success(editingCompanyId ? 'Empresa actualizada.' : 'Empresa creada.');
    const refreshResults = await Promise.allSettled([fetchCompanies(), fetchResponsibles()]);
    const refreshIsCurrent = (
      companyRevision === companyCatalogRevisionRef.current
      && responsibleRevision === responsibleCatalogRevisionRef.current
    );
    if (refreshIsCurrent && refreshResults.some((result) => result.status === 'rejected')) {
      toast.warning('Empresa guardada; no se pudo actualizar todo el catálogo.');
    }
  };

  const submitResponsible = async (event) => {
    event.preventDefault();
    if (responsibleSubmittingRef.current) return;
    responsibleSubmittingRef.current = true;
    setResponsibleSubmitting(true);
    setCatalogError('');
    let response;
    try {
      if (editingResponsibleId) {
        const { company, ...editableFields } = responsibleForm;
        response = await api.patch('/api/audit/credit-responsibles/', { ...editableFields, id: editingResponsibleId });
      } else {
        response = await api.post('/api/audit/credit-responsibles/', responsibleForm);
      }
    } catch (err) {
      const message = err?.response?.data?.error || 'No se pudo guardar el responsable.';
      setCatalogError(message);
      toast.error(message);
      return;
    } finally {
      responsibleSubmittingRef.current = false;
      setResponsibleSubmitting(false);
    }

    const responsibleRevision = responsibleCatalogRevisionRef.current + 1;
    responsibleCatalogRevisionRef.current = responsibleRevision;
    reconcileResponsible(response.data);
    setResponsibleForm(emptyResponsible);
    setEditingResponsibleId(null);
    toast.success(editingResponsibleId ? 'Responsable actualizado.' : 'Responsable creado.');
    const refreshResults = await Promise.allSettled([fetchResponsibles()]);
    if (
      responsibleRevision === responsibleCatalogRevisionRef.current
      && refreshResults.some((result) => result.status === 'rejected')
    ) {
      toast.warning('Responsable guardado; no se pudo actualizar el catálogo.');
    }
  };

  const openCompanyCatalog = () => {
    setCatalogError('');
    setCompanySearch('');
    setCompanyForm(emptyCompany);
    setEditingCompanyId(null);
    setCompanyModalOpen(true);
  };

  const handleTabKeyDown = (event, currentIndex) => {
    let nextIndex;
    if (event.key === 'ArrowRight') nextIndex = (currentIndex + 1) % tabs.length;
    if (event.key === 'ArrowLeft') nextIndex = (currentIndex - 1 + tabs.length) % tabs.length;
    if (event.key === 'Home') nextIndex = 0;
    if (event.key === 'End') nextIndex = tabs.length - 1;
    if (nextIndex === undefined) return;

    event.preventDefault();
    setActiveTab(tabs[nextIndex].key);
    tabRefs.current[nextIndex]?.focus();
  };

  const openResponsibleCatalog = () => {
    setCatalogError('');
    setResponsibleSearch('');
    setResponsibleForm(emptyResponsible);
    setEditingResponsibleId(null);
    setResponsibleModalOpen(true);
  };

  const closeCompanyCatalog = useCallback(() => setCompanyModalOpen(false), []);
  const closeResponsibleCatalog = useCallback(() => setResponsibleModalOpen(false), []);
  const closeReviewModal = useCallback(() => setReviewModal(null), []);
  const closeCollectModal = useCallback(() => setCollectModalOpen(false), []);

  const editCompany = (company) => {
    setCatalogError('');
    setEditingCompanyId(company.id);
    setCompanyForm({
      nombre: company.nombre || '',
      ruc: company.ruc || '',
      email: company.email || '',
      telefono: company.telefono || '',
      activo: Boolean(company.activo),
    });
  };

  const editResponsible = (responsible) => {
    setCatalogError('');
    setEditingResponsibleId(responsible.id);
    setResponsibleForm({
      company: String(responsible.company),
      nombre: responsible.nombre || '',
      departamento: responsible.departamento || '',
      email: responsible.email || '',
      telefono: responsible.telefono || '',
      activo: Boolean(responsible.activo),
    });
  };

  const reviewSelected = async () => {
    if (!reviewModal || selectedRows.length === 0) return;
    try {
      await api.patch('/api/audit/credit-receivables/review/', {
        ids: selectedRows.map((row) => row.id),
        action: reviewModal,
        notes: reviewNotes,
      });
      toast.success(reviewModal === 'aprobar' ? 'Créditos aprobados.' : 'Créditos rechazados.');
      setReviewModal(null);
      setReviewNotes('');
      await fetchReceivables();
    } catch (err) {
      toast.error(err?.response?.data?.error || 'No se pudo revisar la selección.');
    }
  };

  const collectSelected = async (event) => {
    event.preventDefault();
    if (selectedRows.length === 0) {
      toast.error('Selecciona al menos un trámite aprobado.');
      return;
    }
    if (!selectedPeriodLabel) {
      toast.error('Selecciona trámites de un solo mes calculado.');
      return;
    }
    const formData = new FormData();
    selectedRows.forEach((row) => formData.append('ids', row.id));
    formData.append('tipo_pago', collectForm.tipo_pago);
    formData.append('numero_transaccion', collectForm.numero_transaccion);
    formData.append('fecha_pago', collectForm.fecha_pago);
    formData.append('monto_total', selectedTotal.toFixed(2));
    formData.append('notes', collectForm.notes || '');
    if (collectForm.archivo) formData.append('archivo', collectForm.archivo);
    try {
      await api.patch('/api/audit/credit-receivables/collect/', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      toast.success('Pago mensual registrado.');
      setCollectModalOpen(false);
      setCollectForm({
        tipo_pago: 'transferencia',
        numero_transaccion: '',
        fecha_pago: new Date().toISOString().slice(0, 10),
        archivo: null,
        notes: '',
      });
      await fetchReceivables();
    } catch (err) {
      toast.error(err?.response?.data?.error || 'No se pudo registrar el cobro.');
    }
  };

  const handleDownloadReport = useCallback(
    async (format) => {
      const tab = tabs.find((item) => item.key === activeTab) || tabs[0];
      const params = { ...tab.params, period: reportPeriod };
      if (reportPeriod === 'day') {
        if (!filters.fecha_documento) {
          toast.error('Selecciona un día para generar el reporte.');
          return;
        }
        params.date = filters.fecha_documento;
      } else if (reportPeriod === 'month') {
        if (!filters.year || !filters.month) {
          toast.error('Selecciona año y mes para generar el reporte.');
          return;
        }
        params.year = filters.year;
        params.month = filters.month;
      } else if (reportPeriod === 'year') {
        if (!filters.year) {
          toast.error('Selecciona un año para generar el reporte.');
          return;
        }
        params.year = filters.year;
      }
      if (filters.company_id) params.company_id = filters.company_id;
      if (filters.responsible_id) params.responsible_id = filters.responsible_id;
      if (filters.usuario_matrizador) params.usuario_matrizador = filters.usuario_matrizador;
      await downloadReport({
        api,
        endpoint: '/api/audit/reports/credit-receivables/',
        params,
        format,
        baseName: 'reporte-creditos',
      });
    },
    [activeTab, reportPeriod, filters]
  );

  if (loading) return <div className="text-white text-center">Cargando...</div>;
  if (!user) return null;

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="min-h-screen bg-gray-900 text-white p-4 sm:p-6">
      <ToastContainer position="top-right" autoClose={3000} pauseOnHover />

      <header className="flex flex-wrap items-center justify-between gap-4 mb-6">
        <div className="flex items-center gap-3">
          <img src={logo} alt="Notaría 4 Cantón Otavalo" className="w-12 h-auto sm:w-14" />
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold">Trámites por Cobrar</h1>
            <p className="text-sm text-gray-300">Créditos mensuales por matrizador, empresa y responsable</p>
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          <button
            onClick={openCompanyCatalog}
            className="inline-flex items-center gap-2 rounded-lg bg-notaria-blue-2 px-4 py-2 font-semibold hover:bg-notaria-blue-3"
          >
            <PlusCircleIcon className="h-5 w-5" />
            Gestionar empresas
          </button>
          <button
            onClick={openResponsibleCatalog}
            className="inline-flex items-center gap-2 rounded-lg bg-notaria-blue-2 px-4 py-2 font-semibold hover:bg-notaria-blue-3"
          >
            <PlusCircleIcon className="h-5 w-5" />
            Gestionar responsables
          </button>
          <button
            type="button"
            onClick={fetchReceivables}
            aria-label="Actualizar trámites por cobrar"
            title="Actualizar trámites por cobrar"
            className="rounded-lg bg-notaria-blue-2 px-4 py-2 font-semibold hover:bg-notaria-blue-3"
          >
            <ArrowPathIcon className={`h-5 w-5 ${loadingData ? 'animate-spin' : ''}`} />
          </button>
          <Link to="/dashboard/notario" className="rounded-lg bg-notaria-yellow px-4 py-2 font-bold text-notaria-blue-1 hover:bg-notaria-red hover:text-white">
            Volver
          </Link>
        </div>
      </header>

      {alerts && (alerts.overdue_count > 0 || alerts.pending_approval_count > 0) && (
        <section className="mb-6 rounded-lg border border-notaria-yellow/40 bg-notaria-yellow/10 p-4">
          <div className="flex flex-wrap items-center gap-3">
            <ExclamationTriangleIcon className="h-6 w-6 text-notaria-yellow" />
            <p className="font-semibold">
              {alerts.overdue_count} vencidos ({toMoney(alerts.overdue_total)}) · {alerts.pending_approval_count} por aprobar · {alerts.approved_pending_count} aprobados por cobrar
            </p>
          </div>
        </section>
      )}

      <main className="mx-auto max-w-7xl space-y-6">
        <section className="rounded-lg bg-notaria-blue-1/80 p-4 shadow-xl">
          <div className="grid grid-cols-2 gap-2 sm:grid-cols-4" role="tablist" aria-label="Estados de cuentas por cobrar">
            {tabs.map((tab, index) => (
              <button
                key={tab.key}
                ref={(element) => { tabRefs.current[index] = element; }}
                type="button"
                id={`credit-tab-${tab.key}`}
                role="tab"
                aria-selected={activeTab === tab.key}
                aria-controls={`credit-tabpanel-${tab.key}`}
                tabIndex={activeTab === tab.key ? 0 : -1}
                onClick={() => setActiveTab(tab.key)}
                onKeyDown={(event) => handleTabKeyDown(event, index)}
                className={`rounded-lg px-4 py-3 text-sm font-bold transition ${
                  activeTab === tab.key ? 'bg-notaria-yellow text-notaria-blue-1' : 'bg-notaria-blue-2/70 text-white hover:bg-notaria-blue-3'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>

          <div
            id={`credit-tabpanel-${activeTab}`}
            role="tabpanel"
            aria-labelledby={`credit-tab-${activeTab}`}
          >
          <div className="mt-4 grid grid-cols-1 gap-3 md:grid-cols-4">
            <input
              type="date"
              value={filters.fecha_documento}
              onChange={(e) => setFilters((prev) => ({ ...prev, fecha_documento: e.target.value }))}
              className="rounded-lg bg-notaria-blue-2 p-2 text-white"
              aria-label="Filtrar por día"
            />
            <div className="grid grid-cols-2 gap-2">
              <input
                type="number"
                value={filters.year}
                onChange={(e) => setFilters((prev) => ({ ...prev, year: e.target.value }))}
                className="rounded-lg bg-notaria-blue-2 p-2 text-white"
                aria-label="Año"
              />
              <select
                value={filters.month}
                onChange={(e) => setFilters((prev) => ({ ...prev, month: e.target.value }))}
                className="rounded-lg bg-notaria-blue-2 p-2 text-white"
                aria-label="Mes"
              >
                <option value="">Mes</option>
                {monthOptions.map(([value, label]) => (
                  <option key={value} value={value}>{label}</option>
                ))}
              </select>
            </div>
            <select
              value={filters.company_id}
              onChange={(e) => setFilters((prev) => ({ ...prev, company_id: e.target.value, responsible_id: '' }))}
              className="rounded-lg bg-notaria-blue-2 p-2 text-white"
              aria-label="Empresa"
            >
              <option value="">Todas las empresas</option>
              {companies.map((company) => (
                <option key={company.id} value={company.id}>{company.nombre}</option>
              ))}
            </select>
            <select
              value={filters.responsible_id}
              onChange={(e) => setFilters((prev) => ({ ...prev, responsible_id: e.target.value }))}
              className="rounded-lg bg-notaria-blue-2 p-2 text-white"
              aria-label="Responsable"
            >
              <option value="">Todos los responsables</option>
              {responsiblesForFilter.map((responsible) => (
                <option key={responsible.id} value={responsible.id}>
                  {responsible.nombre}{responsible.departamento ? ` - ${responsible.departamento}` : ''}
                </option>
              ))}
            </select>
            <select
              value={filters.usuario_matrizador}
              onChange={(e) => setFilters((prev) => ({ ...prev, usuario_matrizador: e.target.value }))}
              className="rounded-lg bg-notaria-blue-2 p-2 text-white"
              aria-label="Matrizador"
            >
              <option value="">Todos los matrizadores</option>
              {matrizadores.map((matrizador) => (
                <option key={matrizador.nombre} value={normalizeText(matrizador.nombre)}>
                  {matrizador.nombre}
                </option>
              ))}
            </select>
            <input
              type="date"
              value={filters.start_date}
              onChange={(e) => setFilters((prev) => ({ ...prev, start_date: e.target.value }))}
              className="rounded-lg bg-notaria-blue-2 p-2 text-white"
              aria-label="Desde"
            />
            <input
              type="date"
              value={filters.end_date}
              onChange={(e) => setFilters((prev) => ({ ...prev, end_date: e.target.value }))}
              className="rounded-lg bg-notaria-blue-2 p-2 text-white"
              aria-label="Hasta"
            />
            <button
              type="button"
              onClick={fetchReceivables}
              className="rounded-lg bg-notaria-yellow px-4 py-2 font-bold text-notaria-blue-1 hover:bg-notaria-red hover:text-white"
            >
              Aplicar filtros
            </button>
          </div>

          <div className="mt-4 border-t border-white/10 pt-4">
            <p className="mb-3 text-sm font-semibold text-gray-200">Reporte Excel con los filtros actuales</p>
            <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
              <select
                value={reportPeriod}
                onChange={(e) => setReportPeriod(e.target.value)}
                className="rounded-lg bg-notaria-blue-2 p-2 text-white"
                aria-label="Periodo del reporte"
              >
                <option value="day">Por día</option>
                <option value="month">Por mes</option>
                <option value="year">Por año</option>
              </select>
              <button
                type="button"
                onClick={() => handleDownloadReport('xlsx')}
                className="rounded-lg bg-notaria-yellow px-4 py-2 font-bold text-notaria-blue-1 hover:bg-notaria-red hover:text-white"
              >
                Descargar XLSX
              </button>
              <button
                type="button"
                onClick={() => handleDownloadReport('xls')}
                className="rounded-lg bg-notaria-blue-2 px-4 py-2 font-bold text-white hover:bg-notaria-blue-3"
              >
                Descargar XLS
              </button>
            </div>
          </div>
          </div>
        </section>

        <section className="grid grid-cols-1 gap-3 lg:grid-cols-4">
          <div className="rounded-lg bg-notaria-blue-1/80 p-4">
            <p className="text-sm text-gray-300">Total visible</p>
            <p className="text-2xl font-bold">{toMoney(summary?.total)}</p>
            <p className="text-sm text-gray-300">{summary?.count || 0} trámites</p>
          </div>
          <SummaryList title="Por empresa" rows={summary?.by_company || []} />
          <SummaryList title="Por responsable" rows={summary?.by_responsible || []} />
          <SummaryList title="Por matrizador" rows={summary?.by_matrizador || []} />
        </section>

        {selectedRows.length > 0 && (
          <section className="rounded-lg border border-white/10 bg-notaria-blue-1/80 p-4">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div>
                <p className="font-semibold">{selectedRows.length} seleccionados · {toMoney(selectedTotal)}</p>
                <p className="text-sm text-gray-300">{selectedPeriodLabel ? `Mes calculado: ${selectedPeriodLabel}` : 'Selección de varios meses'}</p>
              </div>
              <div className="flex flex-wrap gap-2">
                {activeTab === 'pending' && (
                  <>
                    <button onClick={() => setReviewModal('aprobar')} className="rounded-lg bg-green-600 px-4 py-2 font-bold hover:bg-green-500">Aprobar</button>
                    <button onClick={() => setReviewModal('rechazar')} className="rounded-lg bg-notaria-red px-4 py-2 font-bold hover:bg-red-500">Rechazar</button>
                  </>
                )}
                {(activeTab === 'collect' || activeTab === 'overdue') && (
                  <button onClick={() => setCollectModalOpen(true)} className="inline-flex items-center gap-2 rounded-lg bg-notaria-yellow px-4 py-2 font-bold text-notaria-blue-1 hover:bg-notaria-red hover:text-white">
                    <BanknotesIcon className="h-5 w-5" />
                    Justificar cobro mensual
                  </button>
                )}
              </div>
            </div>
          </section>
        )}

        <section className="rounded-lg bg-notaria-blue-1/80 p-4 shadow-xl">
          <div className="mb-3 flex flex-wrap items-center justify-between gap-3 text-sm text-gray-200">
            <div>
              Mostrando {pageStart}-{pageEnd} de {rows.length} trámites
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <label htmlFor="credit-page-size" className="text-gray-300">Filas</label>
              <select
                id="credit-page-size"
                value={pageSize}
                onChange={(e) => {
                  setPageSize(Number(e.target.value));
                  setCurrentPage(1);
                }}
                className="rounded-lg bg-notaria-blue-2 px-3 py-2 text-white"
              >
                <option value={25}>25</option>
                <option value={50}>50</option>
                <option value={100}>100</option>
              </select>
              <button
                type="button"
                onClick={() => setCurrentPage((page) => Math.max(page - 1, 1))}
                disabled={currentPage <= 1}
                className={`rounded-lg px-3 py-2 font-semibold ${currentPage <= 1 ? 'bg-notaria-blue-2/50 text-gray-400' : 'bg-notaria-blue-2 text-white hover:bg-notaria-blue-3'}`}
              >
                Anterior
              </button>
              <span className="px-2">Página {currentPage} de {totalPages}</span>
              <button
                type="button"
                onClick={() => setCurrentPage((page) => Math.min(page + 1, totalPages))}
                disabled={currentPage >= totalPages}
                className={`rounded-lg px-3 py-2 font-semibold ${currentPage >= totalPages ? 'bg-notaria-blue-2/50 text-gray-400' : 'bg-notaria-blue-2 text-white hover:bg-notaria-blue-3'}`}
              >
                Siguiente
              </button>
            </div>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full border-collapse text-sm">
              <thead>
                <tr className="bg-notaria-blue-2 text-left">
                  <th className="p-3">Sel.</th>
                  <th className="p-3">Mes</th>
                  <th className="p-3">Día</th>
                  <th className="p-3">Vence</th>
                  <th className="p-3">Matrizador</th>
                  <th className="p-3">Empresa</th>
                  <th className="p-3">Responsable</th>
                  <th className="p-3">Trámite</th>
                  <th className="p-3">Monto</th>
                  <th className="p-3">Estado</th>
                </tr>
              </thead>
              <tbody>
                {loadingData ? (
                  <tr><td colSpan="10" className="p-4 text-center text-gray-300">Cargando...</td></tr>
                ) : rows.length === 0 ? (
                  <tr><td colSpan="10" className="p-4 text-center text-gray-400">No hay trámites con los filtros actuales.</td></tr>
                ) : paginatedRows.map((row) => {
                  const responsibleLabel = `${row.responsible?.nombre || ''}${row.responsible?.departamento ? ` - ${row.responsible.departamento}` : ''}`;
                  return (
                    <tr key={row.id} className="border-b border-notaria-blue-2 hover:bg-notaria-blue-2/40">
                      <td className="p-3">
                        {canSelectRow(row) && (
                          <input
                            type="checkbox"
                            checked={selectedIds.has(String(row.id))}
                            onChange={() => toggleSelected(row.id)}
                            className="h-5 w-5 rounded text-notaria-yellow"
                            aria-label={`Seleccionar crédito ${row.tramite?.no_temporal || row.id}`}
                          />
                        )}
                      </td>
                      <td className="p-3">{row.periodo_year}-{String(row.periodo_month).padStart(2, '0')}</td>
                      <td className="p-3">{row.fecha_tramite}</td>
                      <td className="p-3">{row.fecha_vencimiento}</td>
                      <td className="p-3">{row.usuario_matrizador}</td>
                      <td className="p-3">{row.company?.nombre || '-'}</td>
                      <td className="p-3">{responsibleLabel || '-'}</td>
                      <td className="p-3">
                        <div className="max-h-20 overflow-y-auto">
                          <p className="font-semibold">{row.tramite?.no_temporal || '-'}</p>
                          <p className="text-xs text-gray-300">{row.tramite?.acto || '-'}</p>
                        </div>
                      </td>
                      <td className="p-3 font-semibold">{toMoney(row.monto)}</td>
                      <td className="p-3">{row.estado}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </section>
      </main>

      <CatalogModal open={companyModalOpen} title="Gestionar empresas" onClose={closeCompanyCatalog}>
        <div className="grid gap-5 lg:grid-cols-[minmax(0,1fr)_minmax(18rem,0.8fr)]">
          <section className="min-h-0 space-y-3" aria-label="Lista de empresas">
            <label className="relative block">
              <span className="sr-only">Buscar empresas</span>
              <MagnifyingGlassIcon className="pointer-events-none absolute left-3 top-2.5 h-5 w-5 text-gray-400" />
              <input value={companySearch} onChange={(e) => setCompanySearch(e.target.value)} className="w-full rounded-lg bg-notaria-blue-2 py-2 pl-10 pr-3 text-white" placeholder="Buscar empresas" aria-label="Buscar empresas" />
            </label>
            <div className="max-h-[38vh] space-y-2 overflow-y-auto pr-1">
              {filteredCompanies.map((company) => (
                <article key={company.id} className="flex items-start justify-between gap-3 rounded-lg border border-white/10 bg-notaria-blue-2/40 p-3">
                  <div className="min-w-0">
                    <p className="truncate font-semibold">{normalizeText(company.nombre)}</p>
                    <p className="truncate text-xs text-gray-300">{company.ruc || 'SIN RUC'}{company.email ? ` · ${company.email}` : ''}</p>
                    <span className={`mt-2 inline-block text-xs font-semibold ${company.activo ? 'text-emerald-300' : 'text-amber-300'}`}>{company.activo ? 'ACTIVA' : 'INACTIVA'}</span>
                  </div>
                  <button type="button" onClick={() => editCompany(company)} aria-label={`Editar ${normalizeText(company.nombre)}`} title={`Editar ${normalizeText(company.nombre)}`} className="shrink-0 rounded-md p-2 text-notaria-yellow hover:bg-white/10">
                    <PencilSquareIcon className="h-5 w-5" />
                  </button>
                </article>
              ))}
              {filteredCompanies.length === 0 && <p className="py-6 text-center text-sm text-gray-400">Sin empresas coincidentes.</p>}
            </div>
          </section>
          <form onSubmit={submitCompany} className="space-y-3 border-t border-white/10 pt-4 lg:border-l lg:border-t-0 lg:pl-5 lg:pt-0">
            <div className="flex items-center justify-between gap-3">
              <h3 className="font-semibold">{editingCompanyId ? 'Editar empresa' : 'Nueva empresa'}</h3>
              {editingCompanyId && <button type="button" onClick={() => { setEditingCompanyId(null); setCompanyForm(emptyCompany); setCatalogError(''); }} className="text-sm text-notaria-yellow hover:text-white">Nueva empresa</button>}
            </div>
            {catalogError && <p role="alert" className="rounded-md border border-notaria-red/50 bg-notaria-red/15 p-2 text-sm text-red-100">{catalogError}</p>}
            <label className="block text-sm">Nombre empresa<input required aria-label="Nombre empresa" value={companyForm.nombre} onChange={(e) => setCompanyForm((prev) => ({ ...prev, nombre: e.target.value }))} className="mt-1 w-full rounded-lg bg-notaria-blue-2 p-2 text-white" /></label>
            <label className="block text-sm">RUC<input aria-label="RUC empresa" value={companyForm.ruc} onChange={(e) => setCompanyForm((prev) => ({ ...prev, ruc: e.target.value }))} className="mt-1 w-full rounded-lg bg-notaria-blue-2 p-2 text-white" /></label>
            <label className="block text-sm">Email<input type="email" aria-label="Email empresa" value={companyForm.email} onChange={(e) => setCompanyForm((prev) => ({ ...prev, email: e.target.value }))} className="mt-1 w-full rounded-lg bg-notaria-blue-2 p-2 text-white" /></label>
            <label className="block text-sm">Teléfono<input aria-label="Teléfono empresa" value={companyForm.telefono} onChange={(e) => setCompanyForm((prev) => ({ ...prev, telefono: e.target.value }))} className="mt-1 w-full rounded-lg bg-notaria-blue-2 p-2 text-white" /></label>
            <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={companyForm.activo} onChange={(e) => setCompanyForm((prev) => ({ ...prev, activo: e.target.checked }))} aria-label="Empresa activa" />Empresa activa</label>
            <button type="submit" disabled={companySubmitting} className="w-full rounded-lg bg-notaria-yellow py-2 font-bold text-notaria-blue-1 disabled:cursor-not-allowed disabled:opacity-60">
              {companySubmitting ? 'Guardando empresa...' : editingCompanyId ? 'Guardar cambios de empresa' : 'Guardar empresa'}
            </button>
          </form>
        </div>
      </CatalogModal>

      <CatalogModal open={responsibleModalOpen} title="Gestionar responsables" onClose={closeResponsibleCatalog}>
        <div className="grid gap-5 lg:grid-cols-[minmax(0,1fr)_minmax(18rem,0.8fr)]">
          <section className="min-h-0 space-y-3" aria-label="Lista de responsables">
            <label className="relative block">
              <span className="sr-only">Buscar responsables</span>
              <MagnifyingGlassIcon className="pointer-events-none absolute left-3 top-2.5 h-5 w-5 text-gray-400" />
              <input value={responsibleSearch} onChange={(e) => setResponsibleSearch(e.target.value)} className="w-full rounded-lg bg-notaria-blue-2 py-2 pl-10 pr-3 text-white" placeholder="Buscar responsables" aria-label="Buscar responsables" />
            </label>
            <div className="max-h-[38vh] space-y-2 overflow-y-auto pr-1">
              {filteredResponsibles.map((responsible) => (
                <article key={responsible.id} className="flex items-start justify-between gap-3 rounded-lg border border-white/10 bg-notaria-blue-2/40 p-3">
                  <div className="min-w-0">
                    <p className="truncate font-semibold">{normalizeText(responsible.nombre)}</p>
                    <p className="truncate text-xs text-gray-300">{normalizeText(responsible.company_nombre)}{responsible.departamento ? ` · ${normalizeText(responsible.departamento)}` : ''}</p>
                    <span className={`mt-2 inline-block text-xs font-semibold ${responsible.activo ? 'text-emerald-300' : 'text-amber-300'}`}>{responsible.activo ? 'ACTIVO' : 'INACTIVO'}</span>
                  </div>
                  <button type="button" onClick={() => editResponsible(responsible)} aria-label={`Editar ${normalizeText(responsible.nombre)}`} title={`Editar ${normalizeText(responsible.nombre)}`} className="shrink-0 rounded-md p-2 text-notaria-yellow hover:bg-white/10">
                    <PencilSquareIcon className="h-5 w-5" />
                  </button>
                </article>
              ))}
              {filteredResponsibles.length === 0 && <p className="py-6 text-center text-sm text-gray-400">Sin responsables coincidentes.</p>}
            </div>
          </section>
          <form onSubmit={submitResponsible} className="space-y-3 border-t border-white/10 pt-4 lg:border-l lg:border-t-0 lg:pl-5 lg:pt-0">
            <div className="flex items-center justify-between gap-3">
              <h3 className="font-semibold">{editingResponsibleId ? 'Editar responsable' : 'Nuevo responsable'}</h3>
              {editingResponsibleId && <button type="button" onClick={() => { setEditingResponsibleId(null); setResponsibleForm(emptyResponsible); setCatalogError(''); }} className="text-sm text-notaria-yellow hover:text-white">Nuevo responsable</button>}
            </div>
            {catalogError && <p role="alert" className="rounded-md border border-notaria-red/50 bg-notaria-red/15 p-2 text-sm text-red-100">{catalogError}</p>}
            <label className="block text-sm">Empresa del responsable<select required disabled={Boolean(editingResponsibleId)} aria-label="Empresa del responsable" value={responsibleForm.company} onChange={(e) => setResponsibleForm((prev) => ({ ...prev, company: e.target.value }))} className="mt-1 w-full rounded-lg bg-notaria-blue-2 p-2 text-white disabled:cursor-not-allowed disabled:opacity-60"><option value="">Empresa</option>{companies.filter((company) => company.activo || String(company.id) === String(responsibleForm.company)).map((company) => <option key={company.id} value={company.id}>{normalizeText(company.nombre)}</option>)}</select></label>
            <label className="block text-sm">Nombre responsable<input required aria-label="Nombre responsable" value={responsibleForm.nombre} onChange={(e) => setResponsibleForm((prev) => ({ ...prev, nombre: e.target.value }))} className="mt-1 w-full rounded-lg bg-notaria-blue-2 p-2 text-white" /></label>
            <label className="block text-sm">Departamento<input aria-label="Departamento responsable" value={responsibleForm.departamento} onChange={(e) => setResponsibleForm((prev) => ({ ...prev, departamento: e.target.value }))} className="mt-1 w-full rounded-lg bg-notaria-blue-2 p-2 text-white" /></label>
            <label className="block text-sm">Email<input type="email" aria-label="Email responsable" value={responsibleForm.email} onChange={(e) => setResponsibleForm((prev) => ({ ...prev, email: e.target.value }))} className="mt-1 w-full rounded-lg bg-notaria-blue-2 p-2 text-white" /></label>
            <label className="block text-sm">Teléfono<input aria-label="Teléfono responsable" value={responsibleForm.telefono} onChange={(e) => setResponsibleForm((prev) => ({ ...prev, telefono: e.target.value }))} className="mt-1 w-full rounded-lg bg-notaria-blue-2 p-2 text-white" /></label>
            <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={responsibleForm.activo} onChange={(e) => setResponsibleForm((prev) => ({ ...prev, activo: e.target.checked }))} aria-label="Responsable activo" />Responsable activo</label>
            <button type="submit" disabled={responsibleSubmitting} className="w-full rounded-lg bg-notaria-yellow py-2 font-bold text-notaria-blue-1 disabled:cursor-not-allowed disabled:opacity-60">
              {responsibleSubmitting ? 'Guardando responsable...' : editingResponsibleId ? 'Guardar cambios de responsable' : 'Guardar responsable'}
            </button>
          </form>
        </div>
      </CatalogModal>

      <CatalogModal open={Boolean(reviewModal)} title={reviewModal === 'aprobar' ? 'Aprobar créditos' : 'Rechazar créditos'} onClose={closeReviewModal}>
        <div className="space-y-3">
          <p>{selectedRows.length} trámites seleccionados por {toMoney(selectedTotal)}.</p>
          <textarea value={reviewNotes} onChange={(e) => setReviewNotes(e.target.value)} className="w-full rounded-lg bg-notaria-blue-2 p-2 text-white" placeholder="Notas" />
          <button onClick={reviewSelected} className="w-full rounded-lg bg-notaria-yellow py-2 font-bold text-notaria-blue-1">
            Confirmar
          </button>
        </div>
      </CatalogModal>

      <CatalogModal open={collectModalOpen} title="Justificar cobro mensual" onClose={closeCollectModal}>
        <form onSubmit={collectSelected} className="space-y-3">
          <div className="rounded-lg bg-notaria-blue-2 p-3 text-sm">
            <p>Mes calculado: <span className="font-bold">{selectedPeriodLabel || 'Varios meses'}</span></p>
            <p>Total comprobante: <span className="font-bold">{toMoney(selectedTotal)}</span></p>
          </div>
          <select value={collectForm.tipo_pago} onChange={(e) => setCollectForm((prev) => ({ ...prev, tipo_pago: e.target.value }))} className="w-full rounded-lg bg-notaria-blue-2 p-2 text-white">
            <option value="transferencia">Transferencia</option>
            <option value="deposito">Depósito</option>
            <option value="tarjeta_credito">Tarjeta de Crédito</option>
            <option value="nota_credito">Nota de Crédito</option>
          </select>
          <input required value={collectForm.numero_transaccion} onChange={(e) => setCollectForm((prev) => ({ ...prev, numero_transaccion: e.target.value }))} className="w-full rounded-lg bg-notaria-blue-2 p-2 text-white" placeholder="Número de transacción" />
          <input required type="date" value={collectForm.fecha_pago} onChange={(e) => setCollectForm((prev) => ({ ...prev, fecha_pago: e.target.value }))} className="w-full rounded-lg bg-notaria-blue-2 p-2 text-white" />
          <input required type="file" accept=".pdf,.png,.jpg,.jpeg" onChange={(e) => setCollectForm((prev) => ({ ...prev, archivo: e.target.files?.[0] || null }))} className="w-full rounded-lg bg-notaria-blue-2 p-2 text-white" />
          <textarea value={collectForm.notes} onChange={(e) => setCollectForm((prev) => ({ ...prev, notes: e.target.value }))} className="w-full rounded-lg bg-notaria-blue-2 p-2 text-white" placeholder="Notas" />
          <button className="w-full rounded-lg bg-notaria-yellow py-2 font-bold text-notaria-blue-1">Registrar comprobante mensual</button>
        </form>
      </CatalogModal>
    </motion.div>
  );
};

const SummaryList = ({ title, rows }) => (
  <div className="rounded-lg bg-notaria-blue-1/80 p-4">
    <p className="mb-2 text-sm font-semibold text-gray-200">{title}</p>
    <div className="max-h-28 space-y-1 overflow-y-auto pr-1 text-xs">
      {rows.length === 0 ? (
        <p className="text-gray-400">Sin datos</p>
      ) : rows.slice(0, 6).map((row) => (
        <div key={row.key} className="flex justify-between gap-3">
          <span className="truncate">{row.label}</span>
          <span className="shrink-0 font-semibold">{toMoney(row.total)}</span>
        </div>
      ))}
    </div>
  </div>
);

const CatalogModal = ({ open, title, onClose, children }) => {
  const titleId = useId();
  const closeButtonRef = useRef(null);
  const dialogRef = useRef(null);
  const returnFocusRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    returnFocusRef.current = document.activeElement;
    const onKeyDown = (event) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        onClose();
        return;
      }
      if (event.key !== 'Tab') return;

      const focusable = Array.from(dialogRef.current?.querySelectorAll(
        'button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), '
        + 'a[href], [tabindex]:not([tabindex="-1"])'
      ) || []).filter((element) => !element.hasAttribute('hidden'));
      if (focusable.length === 0) {
        event.preventDefault();
        dialogRef.current?.focus();
        return;
      }
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && (document.activeElement === first || !dialogRef.current?.contains(document.activeElement))) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && (document.activeElement === last || !dialogRef.current?.contains(document.activeElement))) {
        event.preventDefault();
        first.focus();
      }
    };
    document.addEventListener('keydown', onKeyDown);
    closeButtonRef.current?.focus();
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      returnFocusRef.current?.focus?.();
    };
  }, [open, onClose]);

  return (
    <AnimatePresence>
      {open && (
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-2 sm:p-4">
          <motion.div ref={dialogRef} role="dialog" aria-modal="true" aria-labelledby={titleId} tabIndex={-1} initial={{ scale: 0.98 }} animate={{ scale: 1 }} exit={{ scale: 0.98 }} className="max-h-[calc(100vh-1rem)] w-full max-w-5xl overflow-y-auto rounded-lg bg-notaria-blue-1 p-4 shadow-2xl sm:max-h-[calc(100vh-2rem)] sm:p-6">
            <div className="mb-4 flex items-center justify-between gap-3">
              <h2 id={titleId} className="text-lg font-bold sm:text-xl">{title}</h2>
              <button ref={closeButtonRef} type="button" onClick={onClose} aria-label={`Cerrar ${title}`} title={`Cerrar ${title}`} className="rounded-md p-1 text-gray-300 hover:bg-white/10 hover:text-notaria-yellow">
                <XMarkIcon className="h-6 w-6" />
              </button>
            </div>
            {children}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default CreditReceivables;
